import axios, { AxiosResponse } from 'axios';
import { Storage } from 'react-jhipster';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { serializeAxiosError } from './reducer.utils';

import { AppThunk } from 'app/config/store';
import { setLocale } from 'app/shared/reducers/locale';

const AUTH_TOKEN_KEY = 'jhi-authenticationToken';

export const initialState = {
  loading: false,
  isAuthenticated: false,
  loginSuccess: false,
  loginError: false, // Errors returned from server side
  showModalLogin: false,
  account: {} as any,
  errorMessage: null as unknown as string, // Errors returned from server side
  redirectMessage: null as unknown as string,
  sessionHasBeenFetched: false,
  logoutUrl: null as unknown as string,
};

export type AuthenticationState = Readonly<typeof initialState>;

// Actions

export const getSession = (): AppThunk<Promise<void>> => async (dispatch, getState) => {
  await dispatch(getAccount());

  const { account } = getState().authentication;
  if (account && account.langKey) {
    const langKey = Storage.session.get('locale', account.langKey);
    dispatch(setLocale(langKey));
  }
};

export const getAccount = createAsyncThunk('authentication/get_account', async () => axios.get<any>('api/account'), {
  serializeError: serializeAxiosError,
});

interface IAuthParams {
  username: string;
  password: string;
  rememberMe?: boolean;
}

export const authenticate = createAsyncThunk(
  'authentication/login',
  async (auth: IAuthParams) => axios.post<any>('api/authenticate', auth),
  {
    serializeError: serializeAxiosError,
  }
);

export const login: (username: string, password: string, rememberMe?: boolean) => AppThunk =
  (username, password, rememberMe = false) =>
  async dispatch => {
    sessionStorage.removeItem('logout');
    const result = await dispatch(authenticate({ username, password, rememberMe }));
    const response = result.payload as AxiosResponse;
    const bearerToken = response?.headers?.authorization;
    if (bearerToken && bearerToken.slice(0, 7) === 'Bearer ') {
      const jwt = bearerToken.slice(7, bearerToken.length);
      if (rememberMe) {
        // I'm using localStorage.set because it will keep the value without "quotes" 1 instead of "1"
        localStorage.setItem('RememberMe', '1');
        // Storage.local.set will save wity "quotes"
        Storage.local.set(AUTH_TOKEN_KEY, jwt);
      } else {
        // if RememberMe is unchecked need to handle the sharing session between tabs. And remove the session when user leaves the site.
        // Sessions are managed with codes in app.tsx and header.tsx
        localStorage.setItem('RememberMe', '0');
        localStorage.setItem('currentSession', Date.now().toString());
        sessionStorage.setItem('currentSession', Date.now().toString());
        sessionStorage.setItem('currentTab', Date.now().toString());
        Storage.local.set(AUTH_TOKEN_KEY, jwt);
        Storage.session.set(AUTH_TOKEN_KEY, jwt);
      }
    }
    dispatch(getSession());
  };

/**
 * Establishes a normal session from a JWT obtained outside the password login flow
 * (the OTP-only transfer link - ALSHMOKH-34). Mirrors `login`'s token-storage behavior
 * so the rest of the app (PrivateRoute, axios interceptor, etc.) treats it identically
 * to a password login.
 */
export const loginWithToken: (idToken: string) => AppThunk<Promise<void>> =
  idToken =>
  async dispatch => {
    sessionStorage.removeItem('logout');
    localStorage.setItem('RememberMe', '0');
    localStorage.setItem('currentSession', Date.now().toString());
    sessionStorage.setItem('currentSession', Date.now().toString());
    sessionStorage.setItem('currentTab', Date.now().toString());
    Storage.local.set(AUTH_TOKEN_KEY, idToken);
    Storage.session.set(AUTH_TOKEN_KEY, idToken);
    await dispatch(getSession());
  };

export const clearAuthToken = () => {
  if (Storage.local.get(AUTH_TOKEN_KEY)) {
    Storage.local.remove(AUTH_TOKEN_KEY);
  }
  if (Storage.session.get(AUTH_TOKEN_KEY)) {
    Storage.session.remove(AUTH_TOKEN_KEY);
  }
};

export const logout: () => AppThunk = () => dispatch => {
  clearAuthToken();
  dispatch(logoutSession());
  sessionStorage.setItem('logout', 'logout');
  Storage.session.remove('account');
};

export const clearAuthentication = messageKey => dispatch => {
  clearAuthToken();
  dispatch(authError(messageKey));
  dispatch(clearAuth());
};

export const AuthenticationSlice = createSlice({
  name: 'authentication',
  initialState: initialState as AuthenticationState,
  reducers: {
    logoutSession() {
      return {
        ...initialState,
        showModalLogin: true,
        sessionHasBeenFetched: true,
      };
    },
    authError(state, action) {
      return {
        ...state,
        showModalLogin: true,
        redirectMessage: action.payload,
        sessionHasBeenFetched: true,
      };
    },
    clearAuth(state) {
      return {
        ...state,
        loading: false,
        showModalLogin: true,
        isAuthenticated: false,
        sessionHasBeenFetched: true,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(authenticate.rejected, (state, action) => ({
        ...initialState,
        errorMessage: action.error.message,
        showModalLogin: true,
        loginError: true,
        sessionHasBeenFetched: true,
      }))
      .addCase(authenticate.fulfilled, state => ({
        ...state,
        loading: false,
        loginError: false,
        showModalLogin: false,
        loginSuccess: true,
      }))
      .addCase(getAccount.rejected, (state, action) => ({
        ...state,
        loading: false,
        isAuthenticated: false,
        sessionHasBeenFetched: true,
        showModalLogin: true,
        errorMessage: action.error.message,
      }))
      .addCase(getAccount.fulfilled, (state, action) => {
        const isAuthenticated = action.payload && action.payload.data && action.payload.data.activated;
        return {
          ...state,
          isAuthenticated,
          loading: false,
          sessionHasBeenFetched: true,
          // The axios interceptor resolves failed requests, so a failed api/account call lands here without data
          account: action.payload?.data || {},
        };
      })
      .addCase(authenticate.pending, state => {
        state.loading = true;
      })
      .addCase(getAccount.pending, state => {
        state.loading = true;
      });
  },
});

export const { logoutSession, authError, clearAuth } = AuthenticationSlice.actions;

// Reducer
export default AuthenticationSlice.reducer;
